import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createTryOnJob,
  deleteTryOnJob,
  deleteTryOnUpload,
  getTryOnJob,
  trackTryOnEvent,
  uploadTryOnImage,
} from '../../services/api/tryOn';
import type { TryOnJobResponse, TryOnProduct } from '../../types/tryOn';
import { parseSizeOptions } from '../chat/ProductSlider/size-utils';
import { t } from '../../utils/ui-strings';
import { ImageUploadStep } from './ImageUploadStep';
import { TryOnErrorState } from './TryOnErrorState';
import {
  TryOnProductDetails,
  decodeAndStripHtml,
  formatDetailMaterials,
  useProductDetail,
} from './TryOnProductDetails';
import {
  clearResultCache,
  clearSessionPhoto,
  getAllCachedResults,
  getAllSessionPhotos,
  getAllSessionUploadIds,
  getCachedProductKeys,
  getCachedResult,
  getSessionConsent,
  getSessionConsentRecord,
  getSessionDevRetentionConsent,
  getSessionPhoto,
  selectedFileFor,
  setCachedResult,
  setSessionConsent,
  setSessionDevRetentionConsent,
  setSessionPhoto,
  type PhotoSelection,
} from './tryOnSession';

/**
 * AI Look Preview-wizard. Fotot — ett per utsnitt, helkropp för plagg och
 * fötterna för skor — är konstanten; plagget eller skon är det som byts:
 * bilderna + färdiga resultat ligger i sessionsminnet (tryOnSession) och
 * överlever både produktbyte OCH modal-stängning — inget laddas upp eller
 * genereras om i onödan.
 *
 * Steg: produkt+storlek → bild → genererar → resultat.
 * (Selfie som ansiktsreferens testades och togs bort — flera ansiktskällor
 * gav sämre likhet än en enda konsekvent helkroppsbild.)
 */

type WizardStep = 'product' | 'photo' | 'processing' | 'result' | 'error';

interface TryOnModalProps {
  product: TryOnProduct;
  /** Alla produkter från senaste renderingen — bytesremsan i resultatvyn. */
  products?: TryOnProduct[];
  isOpen: boolean;
  onClose: () => void;
  /** Sessionsspråket — try-on ärver det från produktkortet. */
  lang?: string;
  /** Samma actionkanal som produktkorten använder (wishlist.add:, cart.add:). */
  onActionClick?: (value: string) => void;
}

/**
 * Samma kontraktsform som cart.add: — den här appen är PRESENTATION, den
 * riktiga önskelistan ägs av live-/Askås-sidan som lyssnar på actionen.
 */
export const WISHLIST_ACTION_PREFIX = 'wishlist.add:';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'expired']);
const POLL_INTERVAL_MS = 2500;
/**
 * Tidskonstant för progressbaren. Bildmodellen ger INGEN riktig progress, så
 * baren är en uppskattning — därför asymptotisk i stället för linjär: den rör
 * sig alltid, saktar in, och når aldrig 100 %.
 *
 * Den linjära varianten slog i sitt tak (95 %) efter 95 s och stod still där.
 * Med gpt-image-2 på quality=high tar en generering längre än så, så frysen
 * syntes varje gång. En höjd konstant flyttar bara frysen; kurvan tar bort
 * den, och behöver inte tunas om nästa gång modell eller kvalitet ändras.
 */
const PROGRESS_TIME_CONSTANT_MS = 45000;
/** Fasetiketterna byts fortfarande på en uppskattad total. */
const EXPECTED_GENERATION_MS = 120000;

/** Nycklar, inte text — språket avgörs vid rendering. */
const PROCESSING_PHASE_KEYS = [
  'try_on_phase_analyzing',
  'try_on_phase_fitting',
  'try_on_phase_rendering',
] as const;

function productKey(product: TryOnProduct): string {
  return product.id || product.url || product.name;
}

/**
 * "Radera min bild nu": varje jobb OCH varje uppladdning i sessionen, inte bara
 * det som visas. En bild som aldrig blev ett jobb låg annars kvar på servern.
 * Sessionen töms även om ett anrop misslyckas — kunden har bett om att glömmas.
 */
export async function deleteSessionTryOnData(resultJobId: string | undefined): Promise<void> {
  const jobIds = new Set(getAllCachedResults().map((cached) => cached.id));
  if (resultJobId) jobIds.add(resultJobId);
  await Promise.all([
    ...[...jobIds].map((jobId) => deleteTryOnJob(jobId).catch(() => undefined)),
    ...getAllSessionUploadIds().map((uploadId) =>
      deleteTryOnUpload(uploadId).catch(() => undefined),
    ),
  ]);
  clearSessionPhoto();
  clearResultCache();
}

/**
 * Backend svarar med maskinläsbara koder (TRY_ON_LOGIN_REQUIRED,
 * TRY_ON_WEEKLY_LIMIT_REACHED…). Kunden ska få veta VAD som behövs — "något gick
 * fel" på en inloggningsgrind skickar folk till supporten i onödan.
 */
export function describeTryOnRequestError(
  error: unknown,
  lang?: string,
  fallbackKey: 'try_on_upload_error' | 'try_on_start_error' = 'try_on_upload_error',
): string {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  const message = String(
    (response?.data as { error?: unknown; message?: unknown } | undefined)?.error ??
      (response?.data as { message?: unknown } | undefined)?.message ??
      '',
  );

  if (message.includes('TRY_ON_LOGIN_REQUIRED') || response?.status === 403) {
    return t('try_on_login_required', lang);
  }
  if (message.includes('TRY_ON_WEEKLY_LIMIT_REACHED') || message.includes('TRY_ON_QUOTA_EXCEEDED') || response?.status === 429) {
    return t('try_on_quota_exceeded', lang);
  }
  if (message.includes('UPLOAD_EXPIRED')) {
    return t('try_on_upload_expired', lang);
  }
  return t(fallbackKey, lang);
}

export function TryOnModal({
  product,
  products,
  isOpen,
  onClose,
  lang,
  onActionClick,
}: TryOnModalProps) {
  const [activeProduct, setActiveProduct] = useState<TryOnProduct>(product);
  const [step, setStep] = useState<WizardStep>('product');
  const [selection, setSelection] = useState<PhotoSelection | null>(null);
  const [hasConsent, setHasConsent] = useState<boolean>(getSessionConsent());
  const [hasDevRetentionConsent, setHasDevRetentionConsent] = useState<boolean>(
    getSessionDevRetentionConsent(),
  );
  const [isStarting, setIsStarting] = useState(false);
  const [job, setJob] = useState<TryOnJobResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sizeByProduct, setSizeByProduct] = useState<Record<string, string>>({});
  const [progressMs, setProgressMs] = useState(0);
  // Bump för att re-läsa sessionscachen efter att ett resultat sparats.
  const [cacheVersion, setCacheVersion] = useState(0);
  // Sparade plagg per produktnyckel — byter kunden plagg i remsan ska knappen
  // spegla just det plagget, inte det som sparades sist.
  const [wishlistedKeys, setWishlistedKeys] = useState<Set<string>>(new Set());
  const startedAtRef = useRef<number | null>(null);

  const framing = activeProduct.framing ?? 'full';
  const selectedFile = selectedFileFor(selection, framing);
  const sessionPhoto = getSessionPhoto(framing);
  const activeKey = productKey(activeProduct);
  const selectedSize = sizeByProduct[activeKey] ?? '';
  const sizeOptions = useMemo(() => parseSizeOptions(activeProduct), [activeProduct]);
  // Förhämtas direkt vid öppning — berikar steg 1 (färg/material/beskrivning)
  // och gör Produktbeskrivning-accordionen i resultatvyn omedelbar.
  const { detail: productDetail } = useProductDetail(activeProduct.id, lang);
  const displayColor = activeProduct.color || productDetail?.color || '';
  const displayMaterial = activeProduct.material || formatDetailMaterials(productDetail);
  const shortDescription = productDetail?.description
    ? decodeAndStripHtml(String(productDetail.description))
    : '';

  const switchableProducts = useMemo(
    () => (products ?? []).filter((p) => p.imageUrl),
    [products],
  );

  // Över alla utsnitt: remsan ska visa färdiga resultat för både plagg och skor,
  // oavsett vilket av dem som är aktivt just nu.
  const cachedKeys = useMemo(
    () => {
      const keys = new Set<string>();
      for (const photo of getAllSessionPhotos()) {
        for (const key of getCachedProductKeys(photo.uploadId)) keys.add(key);
      }
      return keys;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionPhoto?.uploadId, cacheVersion],
  );

  // Nytt öppnings-tillfälle: visa cachat resultat direkt om det finns,
  // annars börja från produktsteget.
  useEffect(() => {
    if (!isOpen) return;
    setActiveProduct(product);
    setJob(null);
    setErrorMessage(null);
    setHasConsent(getSessionConsent());

    const photo = getSessionPhoto(product.framing ?? 'full');
    const cached = photo ? getCachedResult(photo.uploadId, productKey(product)) : null;
    if (cached) {
      setJob(cached);
      setStep('result');
    } else {
      setStep('product');
    }

    void trackTryOnEvent({
      event: 'try_on_opened',
      productId: product.id,
      variantId: product.variantId,
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product.id]);

  // Polla aktivt jobb tills terminal status.
  useEffect(() => {
    if (!job || TERMINAL_STATUSES.has(job.status)) return;

    const intervalId = window.setInterval(async () => {
      try {
        const nextJob = await getTryOnJob(job.id);
        setJob(nextJob);

        if (nextJob.status === 'completed') {
          const photo = getSessionPhoto(activeProduct.framing ?? 'full');
          if (photo) {
            setCachedResult(photo.uploadId, productKey(activeProduct), nextJob);
            setCacheVersion((v) => v + 1);
          }
          setStep('result');
          void trackTryOnEvent({
            event: 'try_on_generation_completed',
            productId: activeProduct.id,
            variantId: activeProduct.variantId,
            jobId: nextJob.id,
            status: nextJob.status,
            elapsedMs: startedAtRef.current ? Date.now() - startedAtRef.current : undefined,
          }).catch(() => undefined);
        } else if (nextJob.status === 'failed' || nextJob.status === 'expired') {
          setStep('error');
          void trackTryOnEvent({
            event: 'try_on_generation_failed',
            productId: activeProduct.id,
            variantId: activeProduct.variantId,
            jobId: nextJob.id,
            status: nextJob.status,
            errorCode: nextJob.errorCode,
          }).catch(() => undefined);
        }
      } catch {
        setStep('error');
        setErrorMessage(t('try_on_status_error', lang));
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [job, activeProduct]);

  // Progressindikator under generering (skalar mot förväntad tid, max 95%).
  useEffect(() => {
    if (step !== 'processing') {
      setProgressMs(0);
      return;
    }
    const intervalId = window.setInterval(() => {
      setProgressMs(startedAtRef.current ? Date.now() - startedAtRef.current : 0);
    }, 500);
    return () => window.clearInterval(intervalId);
  }, [step]);

  const startGeneration = useCallback(
    async (targetProduct: TryOnProduct, uploadId: string) => {
      setStep('processing');
      setErrorMessage(null);
      startedAtRef.current = Date.now();
      try {
        // Endast uploadId. Backend löser kundbilden ur referensen och
        // produktbilden ur katalogen — inga bild-URL:er härifrån.
        const createdJob = await createTryOnJob({
          productId: targetProduct.id,
          variantId: targetProduct.variantId,
          uploadId,
          allowDevRetention: getSessionDevRetentionConsent(),
          consent: getSessionConsentRecord() ?? undefined,
        });
        setJob(createdJob);
      } catch (error) {
        setStep('error');
        // Kvot, inloggning och utgången bild måste synas — inte "något gick fel".
        setErrorMessage(describeTryOnRequestError(error, lang, 'try_on_start_error'));
      }
    },
    [],
  );

  const handleStart = async () => {
    const canUseExisting = !selectedFile && sessionPhoto;
    if ((!selectedFile && !canUseExisting) || !hasConsent || isStarting) return;

    setIsStarting(true);
    setErrorMessage(null);
    try {
      let uploadId = sessionPhoto?.uploadId ?? '';
      if (selectedFile) {
        const upload = await uploadTryOnImage(selectedFile, framing);
        setSessionPhoto(
          {
            uploadId: upload.uploadId,
            previewUrl: upload.previewUrl,
            expiresAt: upload.expiresAt,
          },
          framing,
        );
        // Uppladdad är sessionsbilden sanningen — filen ska inte laddas upp igen.
        setSelection(null);
        uploadId = upload.uploadId;
        void trackTryOnEvent({
          event: 'try_on_image_uploaded',
          productId: activeProduct.id,
          variantId: activeProduct.variantId,
        }).catch(() => undefined);
      }

      setSessionConsent(true);
      setSessionDevRetentionConsent(hasDevRetentionConsent);
      await startGeneration(activeProduct, uploadId);
    } catch (error) {
      setStep('error');
      setErrorMessage(describeTryOnRequestError(error, lang));
    } finally {
      setIsStarting(false);
    }
  };

  const handleSwitchProduct = (nextProduct: TryOnProduct) => {
    const nextKey = productKey(nextProduct);
    if (nextKey === activeKey && step === 'result') return;

    setActiveProduct(nextProduct);
    void trackTryOnEvent({
      event: 'try_on_product_switched',
      productId: nextProduct.id,
      variantId: nextProduct.variantId,
    }).catch(() => undefined);

    const photo = getSessionPhoto(nextProduct.framing ?? 'full');
    const cached = photo ? getCachedResult(photo.uploadId, nextKey) : null;
    if (cached) {
      setJob(cached);
      setStep('result');
      return;
    }

    if (!photo) {
      // Ingen bild för det här utsnittet — första skon efter ett plagg, eller
      // bilden har gått ut.
      setJob(null);
      setSelection(null);
      setStep('photo');
      return;
    }
    void startGeneration(nextProduct, photo.uploadId);
  };

  const handleRetry = () => {
    setJob(null);
    setErrorMessage(null);
    setStep('photo');
  };

  if (!isOpen) return null;

  const canStart = Boolean((selectedFile || sessionPhoto) && hasConsent && !isStarting);
  // 1 - e^(-t/τ): 63 % vid 45 s, 86 % vid 90 s, 95 % vid 135 s, aldrig 100.
  const progressPercent = Math.round(99 * (1 - Math.exp(-progressMs / PROGRESS_TIME_CONSTANT_MS)));
  const phaseIndex = Math.min(
    PROCESSING_PHASE_KEYS.length - 1,
    Math.floor(progressMs / (EXPECTED_GENERATION_MS / PROCESSING_PHASE_KEYS.length)),
  );
  const isWishlisted = wishlistedKeys.has(activeKey);
  const sessionResult = sessionPhoto ? getCachedResult(sessionPhoto.uploadId, activeKey) : null;
  const resultJob = step === 'result' ? (sessionResult ?? job) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-basic-white p-5 shadow-2xl md:p-8">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-grey">
              {t('try_on_eyebrow', lang)}
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-brand-blue">
              {t('try_on_title', lang)}
            </h2>
            <p className="mt-1 text-sm text-text-grey">
              {activeProduct.brand ? `${activeProduct.brand} · ` : ''}
              {activeProduct.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-brand-blue hover:bg-brand-beige-tint-1"
            aria-label={t('try_on_close', lang)}
          >
            ✕
          </button>
        </div>

        {step === 'product' ? (
          <div className="space-y-4">
            <div className="flex gap-4 rounded-2xl bg-brand-beige-tint-1/30 p-4">
              <img
                src={activeProduct.imageUrl}
                alt={activeProduct.name}
                className="h-40 w-32 shrink-0 rounded-xl bg-white object-cover object-top"
              />
              <div className="min-w-0 space-y-1.5">
                {activeProduct.brand ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-blue">
                    {activeProduct.brand}
                  </p>
                ) : null}
                <p className="text-sm font-medium leading-snug text-brand-blue">
                  {activeProduct.name}
                </p>
                {displayColor || displayMaterial ? (
                  <p className="text-xs text-text-grey">
                    {[displayColor, displayMaterial].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
                {productDetail?.fit ? (
                  <p className="text-xs text-text-grey">{productDetail.fit}</p>
                ) : null}
                {activeProduct.price ? (
                  <p className="text-sm font-medium text-brand-blue">
                    {activeProduct.price} {activeProduct.currency ?? ''}
                  </p>
                ) : null}
              </div>
            </div>

            {shortDescription ? (
              <p className="text-xs leading-relaxed text-text-grey [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden">
                {shortDescription}
              </p>
            ) : null}

            {sizeOptions.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-text-grey">
                  {t('select_size', lang)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {sizeOptions.map((size) => {
                    const active = selectedSize === size.rawValue;
                    return (
                      <button
                        key={size.rawValue}
                        type="button"
                        disabled={!size.buyable}
                        onClick={() =>
                          setSizeByProduct((prev) => ({ ...prev, [activeKey]: size.rawValue }))
                        }
                        className={[
                          'min-w-10 rounded-lg border px-3 py-1.5 text-sm transition-colors',
                          active
                            ? 'border-brand-blue bg-brand-blue text-white'
                            : 'border-black/15 bg-white text-brand-blue hover:border-brand-blue',
                          !size.buyable ? 'cursor-not-allowed opacity-40 line-through' : '',
                        ].join(' ')}
                      >
                        {size.displayLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => setStep('photo')}
              className="w-full rounded-xl bg-brand-blue px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-secondary-blue"
            >
              {t('try_on_continue', lang)}
            </button>
          </div>
        ) : null}

        {step === 'photo' ? (
          <div className="space-y-4">
            <ImageUploadStep
              selectedFile={selectedFile}
              existingImageUrl={sessionPhoto?.previewUrl ?? null}
              disabled={isStarting}
              framing={framing}
              onFileSelected={(file) => setSelection(file ? { file, framing } : null)}
              lang={lang}
            />

            <div className="space-y-2">
              <label className="flex gap-3 text-xs text-text-grey">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-brand-blue/30"
                  checked={hasConsent}
                  disabled={isStarting}
                  onChange={(event) => setHasConsent(event.target.checked)}
                />
                <span>
                  {t('try_on_consent_main', lang)}
                </span>
              </label>
              {/* GDPR: förlängd lagring är ett SEPARAT, frivilligt samtycke —
                  får aldrig blockera flödet eller vara förkryssat. */}
              <label className="flex gap-3 text-xs text-text-grey">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-brand-blue/30"
                  checked={hasDevRetentionConsent}
                  disabled={isStarting}
                  onChange={(event) => setHasDevRetentionConsent(event.target.checked)}
                />
                <span>
                  {t('try_on_consent_retention', lang)}
                </span>
              </label>
            </div>

            <button
              type="button"
              disabled={!canStart}
              onClick={handleStart}
              className="w-full rounded-xl bg-brand-blue px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-secondary-blue disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isStarting ? 'Laddar upp…' : 'Starta AI-preview'}
            </button>
          </div>
        ) : null}

        {step === 'processing' ? (
          <div className="rounded-2xl bg-white p-6 text-center shadow-inner">
            <div className="mb-4 flex items-center justify-center gap-3">
              {sessionPhoto?.previewUrl ? (
                <img
                  src={sessionPhoto.previewUrl}
                  alt={t('try_on_alt_your_photo', lang)}
                  className="h-20 w-16 rounded-lg object-cover object-top"
                />
              ) : null}
              <span className="text-lg text-brand-blue">+</span>
              <img
                src={activeProduct.imageUrl}
                alt={activeProduct.name}
                className="h-20 w-16 rounded-lg bg-white object-cover object-top"
              />
            </div>
            <div className="mx-auto mb-2 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-brand-beige-tint-1">
              <div
                className="h-full rounded-full bg-brand-blue transition-[width] duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="mb-2 text-xs tabular-nums text-text-grey">~{progressPercent} %</p>
            <p className="font-medium text-brand-blue">{t(PROCESSING_PHASE_KEYS[phaseIndex], lang)}</p>
            <p className="mt-1 text-sm text-text-grey">
              {t('try_on_generating', lang).replace('{product}', activeProduct.name)}
            </p>
          </div>
        ) : null}

        {step === 'result' && resultJob ? (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[1.6fr_1fr]">
              <div className="rounded-2xl bg-white p-3 shadow-inner">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-grey">
                  {t('try_on_result_title', lang)}
                </p>
                {resultJob.outputImageUrl ? (
                  <img
                    src={resultJob.outputImageUrl}
                    alt={t('try_on_alt_result', lang)}
                    className="aspect-[2/3] w-full rounded-xl object-contain"
                  />
                ) : null}
                {framing === 'feet' ? (
                  <p className="mt-2 text-xs text-text-grey">{t('try_on_feet_disclaimer', lang)}</p>
                ) : null}
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl bg-white p-3 shadow-inner">
                  <img
                    src={activeProduct.imageUrl}
                    alt={activeProduct.name}
                    className="aspect-[4/5] w-full rounded-xl bg-white object-cover object-top"
                  />
                  {activeProduct.brand ? (
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.08em] text-brand-blue">
                      {activeProduct.brand}
                    </p>
                  ) : null}
                  <p className="text-sm text-brand-blue">{activeProduct.name}</p>
                  {displayColor || displayMaterial ? (
                    <p className="mt-0.5 text-xs text-text-grey">
                      {[displayColor, displayMaterial].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                  {activeProduct.price ? (
                    <p className="mt-1 text-sm font-medium text-brand-blue">
                      {activeProduct.price} {activeProduct.currency ?? ''}
                    </p>
                  ) : null}
                  {selectedSize ? (
                    <p className="mt-1 inline-block rounded-md bg-brand-blue px-2 py-0.5 text-xs text-white">
                      {t('try_on_size_label', lang)} {selectedSize}
                    </p>
                  ) : null}
                </div>

                <TryOnProductDetails productId={activeProduct.id} lang={lang} />

                {/* Ordningen är produkt → beskrivning → spara → prova ny bild.
                    Knappen låg först ovanför beskrivningen, men då bröt den
                    produktblocket mitt itu; nu avslutar den produktdelen och
                    "prova ny bild" står kvar som enda bläddra-åtgärd.
                    Modalen stängs INTE — man sparar och fortsätter prova. */}
                {onActionClick ? (
                  <button
                    type="button"
                    disabled={isWishlisted}
                    onClick={() => {
                      onActionClick(`${WISHLIST_ACTION_PREFIX}${activeProduct.id}`);
                      setWishlistedKeys((prev) => new Set(prev).add(activeKey));
                      void trackTryOnEvent({
                        event: 'try_on_wishlist_added',
                        productId: activeProduct.id,
                        variantId: activeProduct.variantId,
                      }).catch(() => undefined);
                    }}
                    className={[
                      'flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition',
                      isWishlisted
                        ? 'cursor-default border-brand-blue bg-brand-blue/10 text-brand-blue'
                        : 'border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white',
                    ].join(' ')}
                  >
                    <span aria-hidden="true">{isWishlisted ? '♥' : '♡'}</span>
                    {isWishlisted ? t('try_on_wishlist_saved', lang) : t('try_on_wishlist_add', lang)}
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={handleRetry}
                  className="w-full rounded-xl border border-brand-blue px-4 py-2.5 text-sm font-medium text-brand-blue transition hover:bg-brand-blue hover:text-white"
                >
                  {t('try_on_new_photo', lang)}
                </button>
              </div>
            </div>

            {switchableProducts.length > 1 ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-text-grey">
                  {t('try_on_switch_garment', lang)}
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {switchableProducts.map((p) => {
                    const key = productKey(p);
                    const isActive = key === activeKey;
                    const hasCached = cachedKeys.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleSwitchProduct(p)}
                        title={
                          hasCached || isActive
                            ? p.name
                            : `${p.name} — genererar ny preview, tar upp till en minut`
                        }
                        className={[
                          'relative w-16 shrink-0 overflow-hidden rounded-lg border-2 transition',
                          isActive
                            ? 'border-brand-blue'
                            : 'border-transparent hover:border-brand-blue/40',
                        ].join(' ')}
                      >
                        <img
                          src={p.imageUrl}
                          alt={p.name}
                          className="aspect-[4/5] w-full bg-white object-cover object-top"
                        />
                        {hasCached && !isActive ? (
                          <span className="absolute bottom-1 right-1 rounded bg-emerald-600 px-1 text-[9px] text-white">
                            ✓
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] text-text-grey">
                  {t('try_on_switch_hint', lang)}
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-brand-beige-tint-1/50 p-3">
              <p className="text-xs text-text-grey">
                {t('try_on_disclaimer', lang).replace(
                  '{retention}',
                  t(
                    getSessionDevRetentionConsent()
                      ? 'try_on_retention_30d'
                      : 'try_on_retention_24h',
                    lang,
                  ),
                )}
              </p>
              <button
                type="button"
                onClick={async () => {
                  await deleteSessionTryOnData(resultJob?.id);
                  setJob(null);
                  setSelection(null);
                  setStep('photo');
                }}
                className="text-xs font-medium text-brand-blue underline underline-offset-2"
              >
                {t('try_on_delete_now', lang)}
              </button>
            </div>
          </div>
        ) : null}

        {step === 'error' ? (
          <TryOnErrorState
            errorCode={job?.errorCode}
            message={errorMessage ?? undefined}
            onRetry={handleRetry}
            lang={lang}
          />
        ) : null}
      </div>
    </div>
  );
}
